import { UuidableName } from 'utils/api/types';
import { ProtectorType } from 'core/constants';
import { UserSearchResponse } from 'utils/api/modules/user/user.types';
import { FormBuilderProperties } from 'utils/api/modules/entities/entities.types';
import Logger from 'utils/logger';
import { publicEventFormDB } from 'utils/indexedDB/publicEventFormDB';
import API from 'utils/api';

const LOG_PREFIX = 'LookupFieldsFetcher';
const verbose = false;

const log = (message: string): void => {
  if (verbose) {
    Logger.info(`${LOG_PREFIX}: ${message}`);
  }
};

/**
 * Lookup fields fetcher for public submission forms
 * Handles detection and fetching of lookup field data for offline use
 */
export class LookupFieldsFetcher {
  private api: typeof API;

  constructor(api: typeof API) {
    this.api = api;
  }

  /**
   * Detect lookup fields from form properties and fetch their data
   */
  async fetchAndStoreLookupFields(
    formId: string,
    formProperties: Record<string, FormBuilderProperties>
  ): Promise<void> {
    try {
      // Detect lookup fields from form properties
      const lookupTypes = this.detectLookupFields(formProperties);

      // No lookup fields found in form
      if (lookupTypes.length === 0) {
        return;
      }

      // Fetch and store data for each lookup type in parallel
      const fetchPromises = lookupTypes.map(async (lookupType) => {
        try {
          const lookupData = await this.fetchLookupData(formId, lookupType);
          // Store the lookup fields data in IndexedDB
          await publicEventFormDB.storeLookupFields(formId, lookupType, lookupData);
        } catch (error) {
          Logger.error(`${LOG_PREFIX}: Failed to fetch lookup data for type ${lookupType}:`, error);
          // Continue with other lookup types even if one fails
        }
      });

      await Promise.all(fetchPromises);
    } catch (error) {
      Logger.error(`${LOG_PREFIX}: Failed to fetch and store lookup fields:`, error);
      // Don't throw error to prevent form loading from failing
    }
  }

  /**
   * Detect lookup fields from form properties
   * Checks for users field as well
   * Extracts lookup types from the nested properties structure
   */
  private detectLookupFields(formProperties: Record<string, FormBuilderProperties>): string[] {
    const lookupTypes = Object.values(formProperties).reduce((set, section) => {
      const properties: FormBuilderProperties[] = Object.values(section.properties);

      properties.forEach((field: FormBuilderProperties) => {
        const fieldProperties: Partial<FormBuilderProperties> = field.properties;
        if (field.questionType === ProtectorType.Lookup && fieldProperties?.lookupType) {
          set.add(fieldProperties.lookupType);
        }

        if (field.questionType === ProtectorType.User) {
          set.add('users');
        }
      });

      return set;
    }, new Set<string>());

    return Array.from(lookupTypes);
  }

  /**
   * Fetch lookup data for a specific entity type
   */
  private async fetchLookupData(formId: string, entityType: string): Promise<Array<UserSearchResponse | UuidableName>> {
    try {
      log(`Fetching lookup data for ${entityType}...`);

      const allFields: Array<UserSearchResponse | UuidableName> = [];
      let offset = 0;
      const limit = 100; // Fetch in batches
      let hasMore = true;

      // Fetch all entities in batches using recursive approach to avoid await in loop
      const fetchBatch = async (): Promise<void> => {
        if (!hasMore) return;

        let entities: UserSearchResponse[] | UuidableName[] = [];

        // Handle user lookups differently - they use different API endpoints
        if (entityType === 'users' || entityType === 'user') {
          // For users, we need to fetch from the users search endpoint
          const { data: userEntities } = await API.user.searchUsers({
            limit,
            public_uuid: formId,
          })();

          entities = userEntities;
        } else {
          // For other entity types, use the standard search
          const { data: entityData } = await this.api.entities.searchEntity(entityType, undefined, {
            limit,
            offset,
            public_uuid: formId,
          })();

          entities = entityData;
        }

        if (entities.length === 0) {
          hasMore = false;
          return;
        }

        allFields.push(...entities);
        offset += limit;

        // If we got fewer results than the limit, we've reached the end
        if (entities.length < limit) {
          hasMore = false;
          return;
        }

        // Recursively fetch the next batch
        await fetchBatch();
      };

      await fetchBatch();

      log(`Fetched ${allFields.length} lookup fields for ${entityType}`);
      return allFields;
    } catch (error) {
      Logger.error(`${LOG_PREFIX}: Failed to fetch lookup data for ${entityType}:`, error);
      throw error;
    }
  }
}
